import { Button, CloseButton } from '@components/utility/buttons';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Fade,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { RLAVRMDialogSchema } from '@rla/schemas';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const VRMFormDialog = ({
    onSuccess,
    ...props
}: DialogProps & {
    onSuccess: () => void;
}) => {
    const [mode, setMode] = useState<'questions' | 'requirements_not_met'>('questions');
    const intl = useIntl();

    const validationSchema = useMemo(() => RLAVRMDialogSchema(intl), [intl]);
    const formik = useFormik({
        initialValues: {
            question1: null,
            question2: null,
        },
        validationSchema,
        onSubmit: (values) => {
            if (values.question1 === 'no' || values.question2 === 'no') {
                setMode('requirements_not_met');
            } else {
                props.onClose?.({}, 'backdropClick');
                onSuccess();
            }
        },
    });

    const handleDialogClose: NonNullable<DialogProps['onClose']> = (event, reason) => {
        props.onClose?.(event, reason);
        formik.resetForm();
    };

    return (
        <Dialog
            {...props}
            keepMounted
            slotProps={{
                transition: {
                    onExited: () => {
                        setMode('questions');
                        formik.resetForm();
                    },
                },
                paper: {
                    sx: {
                        minWidth: 0,
                        maxWidth: 450,
                    },
                },
            }}
            onClose={handleDialogClose}
            maxWidth="xs"
            fullWidth
            component="form"
            onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit();
            }}
        >
            <CloseButton onClick={(event) => handleDialogClose(event, 'escapeKeyDown')} />
            <DialogTitle display="flex" alignItems="center" gap="18px">
                <Box
                    width={50}
                    height={50}
                    borderRadius="5px"
                    bgcolor="neutral-100.main"
                    flexShrink={0}
                    className="black-border"
                    alignItems="center"
                    justifyContent="center"
                    display="flex"
                >
                    <DescriptionOutlinedIcon sx={{ fontSize: 30 }} color="primary" />
                </Box>
                <Typography fontFamily="museo-sans" fontSize={18} fontWeight={600}>
                    <FormattedMessage id="rlaForm.title" defaultMessage="Recover Lost Access Form" />
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Fade in key={mode}>
                    <Box>
                        {mode === 'questions' ? (
                            <>
                                <Typography color="gray-600" fontSize={14} mb="24px">
                                    <FormattedMessage
                                        id="rla.vrmDialog.questions.description"
                                        defaultMessage="Before accessing the VRM form, please answer the following questions."
                                    />
                                </Typography>
                                <Box mb="24px">
                                    <Typography fontFamily="museo-sans" fontWeight={600}>
                                        <FormattedMessage
                                            id="rla.vrmDialog.questions.question1"
                                            defaultMessage="1. Did you attempt to add the account and wait for the current admin's approval?"
                                        />
                                    </Typography>
                                    <FormControl error={!!formik.errors.question1} fullWidth>
                                        <RadioGroup {...formik.getFieldProps('question1')}>
                                            <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label={<FormattedMessage id="common.labels.yes" defaultMessage="Yes" />}
                                            />
                                            <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label={<FormattedMessage id="common.labels.no" defaultMessage="No" />}
                                            />
                                        </RadioGroup>
                                        <FormHelperText>
                                            {formik.touched.question1 && formik.errors.question1}
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography fontFamily="museo-sans" fontWeight={600}>
                                        <FormattedMessage
                                            id="rla.vrmDialog.questions.question2"
                                            defaultMessage="2. If you already submitted a request, have you checked its status to see if it was completed?"
                                        />
                                    </Typography>
                                    <FormControl error={!!formik.errors.question2} fullWidth>
                                        <RadioGroup {...formik.getFieldProps('question2')}>
                                            <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label={<FormattedMessage id="common.labels.yes" defaultMessage="Yes" />}
                                            />
                                            <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label={<FormattedMessage id="common.labels.no" defaultMessage="No" />}
                                            />
                                        </RadioGroup>
                                        <FormHelperText>
                                            {formik.touched.question2 && formik.errors.question2}
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                            </>
                        ) : (
                            <Typography color="gray-600" fontSize={14}>
                                <FormattedMessage
                                    id="rla.vrmDialog.requirementsNotMet.description"
                                    defaultMessage="It seems you do not meet the requirements to access the VRM form. 
                                    You must attempt to add the account for admin approval and verify your request status 
                                    before you can proceed."
                                />
                            </Typography>
                        )}
                    </Box>
                </Fade>
            </DialogContent>
            <DialogActions className="black-border border-0 border-t p-[16px] pt-[24px] bg-[#F9FAFB]" disableSpacing>
                <Fade in key={`${mode}-actions`}>
                    <Box className="w-full flex flex-col gap-[12px]">
                        {mode === 'questions' ? (
                            <>
                                <Button fullRounded fullWidth type="submit">
                                    <FormattedMessage id="common.submit.lowercase" defaultMessage="Submit" />
                                </Button>
                                <Button.Neutral
                                    size="large"
                                    fullRounded
                                    fullWidth
                                    className="bg-white"
                                    onClick={(event) => handleDialogClose(event, 'escapeKeyDown')}
                                >
                                    <FormattedMessage id="common.cancel.lowercase" defaultMessage="Cancel" />
                                </Button.Neutral>
                            </>
                        ) : (
                            <Button
                                fullRounded
                                fullWidth
                                onClick={(event) => handleDialogClose(event, 'escapeKeyDown')}
                            >
                                <FormattedMessage id="common.iUnderstand" defaultMessage="I understand" />
                            </Button>
                        )}
                    </Box>
                </Fade>
            </DialogActions>
        </Dialog>
    );
};
